import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { perimetreService } from './perimetreAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { listPerimetreFiles, toBlobFile } from '../../services/perimetreFilesAPI';
import { LOADING_STATES, SUCCESS_MESSAGES, NOTIFICATION_SEVERITY } from '../../constants';
import { setStatusBar } from '../modals/modalSlice';
import type { RootState } from '../../app/store';
import { Perimetre } from '../../types/api';
import { executeProposal } from '../chat/chatSlice';

export interface PerimetresState {
  data: Perimetre[];
  activePerimetre: Perimetre | string | null;
  globalActivePerimetre: Perimetre | null;
  status: string;
  blobList: any[];
  blobListFetchedAt: number | null;
  activeButton: string;
  activePerimRelationTab: string | null;
  searchPerim: string;
  error?: string;
}

const initialState: PerimetresState = {
  data: [],
  activePerimetre: null,
  globalActivePerimetre: null,
  status: LOADING_STATES.IDLE,
  blobList: [],
  blobListFetchedAt: null,
  activeButton: 'list',
  activePerimRelationTab: null,
  searchPerim: '',
};

/**
 * Durée de vie considérée "fraîche" pour le cache local `blobList` : les URLs de
 * lecture (SAS) qu'il contient expirent au bout de 15 minutes côté backend
 * (`buildReadUrl`). On rafraîchit donc le cache après 10 minutes pour laisser
 * une marge confortable avant l'expiration réelle des SAS déjà récupérées.
 */
export const BLOB_LIST_TTL_MS = 10 * 60_000;

export function isBlobListStale(fetchedAt: number | null): boolean {
  if (fetchedAt === null) return true;
  return Date.now() - fetchedAt > BLOB_LIST_TTL_MS;
}

export const perimetreAsync = createAsyncThunk(
  'perimetres/get',
  async (instance: any) => {
    let accessToken = await getTokenRedirect(tokenRequest, instance);
    if (accessToken) {
      return await perimetreService.perimetre(accessToken);
    }
    return []; // Retourner un tableau vide en cas d'échec
  }
);

export const fetchBlobListAsync = createAsyncThunk(
  'perimetres/fetchBlobList',
  async (instance: any) => {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) return [];
    const files = await listPerimetreFiles(accessToken);
    return files.map(toBlobFile);
  }
);

export const saveAsync = createAsyncThunk(
  'perimetres/save',
  async (data: { instance: any; formPerimetreData: any }, { dispatch }) => {
    const { instance, formPerimetreData } = data;
    let accessToken = await getTokenRedirect(tokenRequest, instance);
    if (accessToken) {
      const result = await perimetreService.save(formPerimetreData, accessToken);
      const isNew = !formPerimetreData._id;
      const message = isNew ? SUCCESS_MESSAGES.CREATED : SUCCESS_MESSAGES.UPDATED;
      dispatch(setStatusBar({
        open: true,
        message,
        severity: NOTIFICATION_SEVERITY.SUCCESS,
      }));
      return result;
    }
  }
);

/**
 * Enregistre la description d'un dossier partagé avec moi (participant
 * « modification », décision du 30/09). Volontairement sans cas dans le
 * reducer : le dossier appartient à son propriétaire et n'entre jamais dans
 * `data` (mes dossiers) — la fiche se relit à chaque ouverture.
 */
export const saveSharedDescriptionAsync = createAsyncThunk(
  'perimetres/saveSharedDescription',
  async (data: { instance: any; perimetreId: string; description: string }, { dispatch }) => {
    const { instance, perimetreId, description } = data;
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error("Jeton d'authentification manquant");
    const result = await perimetreService.saveSharedDescription(perimetreId, description, accessToken);
    dispatch(setStatusBar({
      open: true,
      message: SUCCESS_MESSAGES.PERIMETRE_UPDATED,
      severity: NOTIFICATION_SEVERITY.SUCCESS,
    }));
    return result;
  }
);

export const removeAsync = createAsyncThunk(
  'perimetres/remove',
  async (data: { instance: any; perimetre: Perimetre }, { dispatch, rejectWithValue }) => {
    const { instance, perimetre } = data;
    try {
      const accessToken = await getTokenRedirect(tokenRequest, instance);
      if (!accessToken) {
        return rejectWithValue('Token d\'authentification manquant');
      }
      
      const result = await perimetreService.delete(perimetre._id, accessToken);
      
      dispatch(setStatusBar({
        open: true,
        message: SUCCESS_MESSAGES.DELETED,
        severity: NOTIFICATION_SEVERITY.SUCCESS,
      }));
      
      return { perimetreId: perimetre._id, result };
    } catch (error: any) {
      console.error('[removeAsync] Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur inconnue';
      return rejectWithValue(errorMessage);
    }
  }
);

export const perimetreSlice = createSlice({
  name: 'perimetres',
  initialState,
  reducers: {
    setPerimetres(state, action: PayloadAction<Perimetre[]>) {
      state.data = action.payload;
      state.status = LOADING_STATES.SUCCEEDED;
    },
    setLoading(state) {
      state.status = LOADING_STATES.LOADING;
    },
    setError(state, action: PayloadAction<string>) {
      state.status = LOADING_STATES.FAILED;
      state.error = action.payload;
    },
    setSucceeded(state) {
      state.status = LOADING_STATES.SUCCEEDED;
    },
    activatePerimetre(state, action: PayloadAction<Perimetre | string>) {
      const newPerimetre = action.payload;
      const currentPerimetre = state.activePerimetre;
  const newId = typeof newPerimetre === 'string' ? newPerimetre : (newPerimetre?._id || newPerimetre?.name);
  const currentId = typeof currentPerimetre === 'string' ? currentPerimetre : (currentPerimetre?._id || currentPerimetre?.name);
      if (newId !== currentId) {
        state.activePerimetre = action.payload;
      }
    },
    setGlobalActivePerimetre(state, action: PayloadAction<Perimetre | null>) {
      const newPerimetre = action.payload;
      const currentGlobalPerimetre = state.globalActivePerimetre;
      const newId = newPerimetre?._id || newPerimetre?.name;
      const currentId = currentGlobalPerimetre?._id || currentGlobalPerimetre?.name;
      
      console.log('🔄 [Redux] setGlobalActivePerimetre called:', {
        previousValue: currentGlobalPerimetre,
        previousType: typeof currentGlobalPerimetre,
        newValue: newPerimetre,
        newType: typeof newPerimetre,
        newId,
        currentId,
        willUpdate: newId !== currentId
      });
      
      if (newId !== currentId) {
        state.globalActivePerimetre = action.payload;
        console.log('✅ [Redux] globalActivePerimetre updated to:', action.payload);
      } else {
        console.log('ℹ️ [Redux] No update needed (same ID)');
      }
    },
    storeBlobList(state, action: PayloadAction<any[]>) {
      state.blobList = action.payload;
    },
    setActiveButton(state, action: PayloadAction<string>) {
      state.activeButton = action.payload;
    },
    setActivePerimRelationtab(state, action: PayloadAction<string | null>) {
      state.activePerimRelationTab = action.payload;
    },
    setSearchPerim(state, action: PayloadAction<string>) {
      state.searchPerim = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(perimetreAsync.fulfilled, (state, action) => {
        state.data = action.payload || [];
        state.status = LOADING_STATES.SUCCEEDED;
      })
      .addCase(perimetreAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
      })
      .addCase(perimetreAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = action.error.message;
      })
      .addCase(fetchBlobListAsync.fulfilled, (state, action) => {
        state.blobList = action.payload;
        state.blobListFetchedAt = Date.now();
      })
      .addCase(fetchBlobListAsync.rejected, (state, action) => {
        // Rafraîchissement d'arrière-plan : on enregistre l'erreur sans toucher à
        // `status`, partagé avec la liste des périmètres (pas de spinner global).
        console.error('[fetchBlobListAsync] Erreur lors du chargement des fichiers:', action.error);
        state.error = action.error.message;
      })
      .addCase(removeAsync.fulfilled, (state, action) => {
        // Supprimer le périmètre de la liste
        const perimetreToRemove = action.meta.arg.perimetre;
        if (perimetreToRemove && perimetreToRemove._id) {
          state.data = state.data.filter(i => i._id !== perimetreToRemove._id);
        }
        state.status = LOADING_STATES.SUCCEEDED;
      })
      .addCase(removeAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
      })
      .addCase(removeAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = typeof action.payload === 'string' ? action.payload : action.error.message;
      })
      .addCase(saveAsync.fulfilled, (state, action) => {
        if (!action.payload) return state;
        const payload = action.payload!;
        const existingPerimetreIndex = state.data.findIndex(l => l._id === payload._id);
        if (existingPerimetreIndex > -1) {
          state.data[existingPerimetreIndex] = payload;
        } else {
          state.data.push(payload);
        }
        state.status = LOADING_STATES.SUCCEEDED;
      })
      .addCase(saveAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
      })
      .addCase(saveAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = action.error.message;
      })

      // ── Cross-slice : périmètre créé/modifié/supprimé via une proposition RAG Écriture ──
      // Sans ça, valider une action dans l'Assistant écrivait en base mais laissait
      // `data` intact : il fallait rafraîchir la page pour voir le périmètre.
      .addCase(executeProposal.fulfilled, (state, action) => {
        const result = action.payload;
        if (!result.success || result.targetType !== 'perimetre') return;

        const resultData = result.resultData as Perimetre | undefined;
        const targetId = resultData?._id || result.resultId;
        if (!targetId) return;

        // `activePerimetre` contient soit l'objet, soit son seul identifiant
        const activeId = typeof state.activePerimetre === 'string'
          ? state.activePerimetre
          : state.activePerimetre?._id;

        if (result.action === 'delete') {
          state.data = state.data.filter(p => p._id !== targetId);
          if (activeId === targetId) {
            state.activePerimetre = null;
          }
          if (state.globalActivePerimetre?._id === targetId) {
            state.globalActivePerimetre = null;
          }
          return;
        }

        if (!resultData) return;

        const idx = state.data.findIndex(p => p._id === targetId);
        if (idx > -1) {
          state.data[idx] = resultData;
        } else if (state.status !== LOADING_STATES.IDLE) {
          // Liste jamais chargée : ne rien injecter, `usePerimetresData` la chargera
          // entièrement (`shouldLoadPerimetres` déclenche tant que le status est idle).
          state.data.push(resultData);
        }

        if (activeId === targetId && typeof state.activePerimetre !== 'string') {
          state.activePerimetre = resultData;
        }
        if (state.globalActivePerimetre?._id === targetId) {
          state.globalActivePerimetre = resultData;
        }
      });
  },
});

// Selectors
export const getPerimetres = (state: RootState) => state.perimetres.data;
export const getPerimetresStatus = (state: RootState) => state.perimetres.status;
export const getActivePerimetre = (state: RootState) => state.perimetres.activePerimetre;
export const getGlobalActivePerimetre = (state: RootState) => state.perimetres.globalActivePerimetre;
export const getBlobList = (state: RootState) => state.perimetres.blobList;
export const getBlobListFetchedAt = (state: RootState) => state.perimetres.blobListFetchedAt;
export const getActiveButton = (state: RootState) => state.perimetres.activeButton;
export const getActivePerimRelationTab = (state: RootState) => state.perimetres.activePerimRelationTab;
export const getSearchPerim = (state: RootState) => state.perimetres.searchPerim;

export default perimetreSlice.reducer;
export const {
  setPerimetres,
  setLoading,
  setError,
  setSucceeded,
  activatePerimetre,
  setGlobalActivePerimetre,
  storeBlobList,
  setActiveButton,
  setActivePerimRelationtab,
  setSearchPerim,
} = perimetreSlice.actions;
